import {
  Button,
  ButtonGroup,
  Popover,
  useDisclosure,
  RadioGroup,
  Stack,
  Text,
  NativeSelect,
  Icon,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { CrashType } from "@keita/lanepatrol-sdk";
import { tScoreType } from "../../../../coding/utils/score-utils";
import { CustomPopover } from "../../../../shared/views/CustomPopover";
import { ChevronDownIcon } from "../../../../shared/views/icons";
import {
  HlaColoring,
  HlaColoringType,
} from "../../../../map/models/HlaColoring";
import { HlaAttributeName } from "../../../../map/models/HlaAttribute";
import { useStores } from "../../../../shared/stores/RootStore";
import { observer } from "mobx-react";

const crashTypes = Object.values(CrashType);
const hlaColoringTypes = Object.values(HlaColoringType);
const defaultScore = "global";
const defaultAtribute = HlaAttributeName.Type;
const allAttributes = Object.values(HlaAttributeName);

const HlaColoringSelectorBase = ({
  value,
  onChange,
}: {
  value: HlaColoring;
  onChange: (hlaColoring: HlaColoring) => void;
}) => {
  const { projectStore } = useStores();
  const popover = useDisclosure();
  const [hlaColoring, setHlaColoring] = useState(value);

  useEffect(() => {
    if (popover.open) setHlaColoring(value);
    //eslint-disable-next-line
  }, [popover.open]);

  const changeType = (type: HlaColoringType) => {
    if (HlaColoringType.Score === type) {
      setHlaColoring({ type, scoreKey: defaultScore });
    } else if (HlaColoringType.Attribute === type) {
      setHlaColoring({ type, attributeName: defaultAtribute });
    }
  };

  const submit = () => {
    onChange(hlaColoring);
    popover.onClose();
  };

  return (
    <CustomPopover
      open={popover.open}
      trigger={
        <Button
          colorPalette="primary"
          fontSize={"sm"}
          size="sm"
          variant="plain"
          onClick={popover.onToggle}
        >
          <Text lineClamp={1} whiteSpace="pre-line">
            {HlaColoringType.Score === value.type &&
              (value.scoreKey === "global"
                ? "Global score"
                : tScoreType(value.scoreKey))}
            {HlaColoringType.Attribute === value.type &&
              t(`shared:hlaAttribute.${value.attributeName}`, {
                speedUnit: projectStore.speedUnitStr,
              })}
          </Text>
          <Icon as={ChevronDownIcon} />
        </Button>
      }
      onClose={popover.onClose}
      width={400}
      maxH="360px"
    >
      {popover.open && (
        <>
          <Popover.Header>Line coloring based on...</Popover.Header>
          <Popover.Body overflowY="auto">
            <NativeSelect.Root mb={2}>
              <NativeSelect.Field
                onChange={(event) =>
                  changeType(event.target.value as HlaColoringType)
                }
                value={hlaColoring.type}
              >
                {hlaColoringTypes.map((type, i) => (
                  <option value={type} key={i}>
                    {type}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            {HlaColoringType.Score === hlaColoring.type && (
              <RadioGroup.Root
                py={2}
                value={hlaColoring.scoreKey}
                onValueChange={({ value }) => {
                  setHlaColoring({
                    type: HlaColoringType.Score,
                    scoreKey: value as CrashType,
                  });
                }}
                size="sm"
                colorPalette={"primary"}
              >
                <Stack>
                  <RadioGroup.Item key="global" value="global">
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText>Global score</RadioGroup.ItemText>
                  </RadioGroup.Item>
                  {crashTypes.map((key, i) => (
                    <RadioGroup.Item key={i} value={key}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>
                        {tScoreType(key)}
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </Stack>
              </RadioGroup.Root>
            )}

            {HlaColoringType.Attribute === hlaColoring.type && (
              <RadioGroup.Root
                py={2}
                value={hlaColoring.attributeName}
                size="sm"
                colorPalette={"primary"}
                onValueChange={({ value }) => {
                  setHlaColoring({
                    type: HlaColoringType.Attribute,
                    attributeName: value as HlaAttributeName,
                  });
                }}
              >
                <Stack>
                  {allAttributes.map((attribute, i) => (
                    <RadioGroup.Item key={i} value={attribute}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>
                        {t(`shared:hlaAttribute.${attribute}`, {
                          speedUnit: projectStore.speedUnitStr,
                        })}
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </Stack>
              </RadioGroup.Root>
            )}
          </Popover.Body>
          <Popover.Footer display="flex" justifyContent="flex-end">
            <ButtonGroup colorPalette={"primary"} size={"sm"}>
              <Button onClick={submit}>{t("map:filters.submitButton")}</Button>
            </ButtonGroup>
          </Popover.Footer>
        </>
      )}
    </CustomPopover>
  );
};

export const HlaColoringSelector = observer(HlaColoringSelectorBase);
